<template>
  <div class="w-100 infinite-scroll-trigger">
    <slot v-if="isLoading && showSpinner">
      <div class="w-100 py-3 text-center">
        <b-spinner small />
      </div>
    </slot>
  </div>
</template>

<script>
  // Sentinels only passed over during a smooth scroll must not load, or content shifts above the viewport.
  const DWELL_MS = 150

  export default {
    name: 'InfiniteScrollTrigger',
    props: {
      hasMore: {
        type: Boolean,
        default: true
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      isPaused: {
        type: Boolean,
        default: false
      },
      showSpinner: {
        type: Boolean,
        default: true
      },
      rootMargin: {
        type: String,
        default: '200px 0px'
      }
    },
    data() {
      return {
        isIntersecting: false,
        dwellTimer: null,
        observer: null,
        lastGrowableHeight: 0
      }
    },
    watch: {
      isLoading(isNowLoading) {
        this.$nextTick(() => {
          this.preserveScrollPosition()
          if (!isNowLoading) this.reobserve()
        })
      },
      isPaused(isNowPaused) {
        if (isNowPaused) return
        this.lastGrowableHeight = this.getGrowableHeight()
        this.reobserve()
      }
    },
    mounted() {
      this.lastGrowableHeight = this.getGrowableHeight()
      this.observer = new IntersectionObserver(this.handleIntersect, { rootMargin: this.rootMargin })
      this.observer.observe(this.$el)
    },
    beforeDestroy() {
      clearTimeout(this.dwellTimer)
      if (this.observer) this.observer.disconnect()
    },
    methods: {
      handleIntersect(entries) {
        this.isIntersecting = entries[entries.length - 1].isIntersecting
        clearTimeout(this.dwellTimer)
        if (!this.isIntersecting) return
        this.dwellTimer = setTimeout(this.emitReachIfVisible, DWELL_MS)
      },
      emitReachIfVisible() {
        if (!this.isIntersecting || this.isLoading || this.isPaused || !this.hasMore) return
        this.$emit('reach')
      },
      reobserve() {
        // Re-observing yields a fresh entry, so a sentinel still in view after a short page fires again.
        if (!this.observer) return
        this.observer.unobserve(this.$el)
        this.observer.observe(this.$el)
      },
      getGrowableList() {
        // The trigger sits directly after the list it grows.
        return this.$el.previousElementSibling
      },
      getGrowableHeight() {
        const list = this.getGrowableList()
        const listHeight = list ? list.offsetHeight : 0
        return listHeight + this.$el.offsetHeight
      },
      preserveScrollPosition() {
        const list = this.getGrowableList()
        const heightBefore = this.lastGrowableHeight
        const heightDelta = this.getGrowableHeight() - heightBefore
        this.lastGrowableHeight += heightDelta
        if (!list || heightDelta === 0 || this.isPaused) return

        // Growth in the upper half of the viewport would push what the user is reading, so shift the scroll with it.
        const growthTop = list.getBoundingClientRect().top + heightBefore
        if (growthTop >= window.innerHeight / 2) return
        window.scrollBy(0, heightDelta)
      }
    }
  }
</script>

<style scoped>
  .infinite-scroll-trigger {
    min-height: 1px;
  }
</style>



// WEBPACK FOOTER //
// src/components/v2/InfiniteScrollTrigger.vue